import { FC, useCallback, useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useIsFocused } from 'react-navigation-hooks';
import safeAwait from 'safe-await';

import Modal from '../../utils/packages/reactNativeModal';
import IconCancel from '../../assets/svg/IconCancel';
import { AnalyticsEvent } from '../../constants/AnalyticsEvent';
import { maskBackgroundColor, neutralDark } from '../../constants/color';
import { isCypress } from '../../constants/Cypress';
import { CLOSE_WEBPUSH_SUBSCRIBE_MODAL } from '../../constants/guideKeys';
import { isBot } from '../../constants/misc';
import { AuthProps } from '../../decorators/withAuth';
import useActions from '../../hooks/useActions';
import { translate } from '../../i18n';
import { actionsList } from '../../redux/rootAction';
import {
  registerBrowserToken,
  saveBrowserToken,
} from '../../utils/analytics/iterableFactory/iterableEvents';
import { getFCMToken, isSupportedWebPush } from '../../utils/packages/firebase';
import { useMakeHasReadGuideByKey } from './hooks';
import { S } from './styles';

const shouldShowWebPushSubscribeModal = () => {
  return (
    !isBot &&
    !isCypress &&
    isSupportedWebPush() &&
    window.Notification.permission === 'default'
  );
};

export interface Props extends AuthProps {}

const WebPushSubscribeModal: FC<Props> = ({ memberInfo }) => {
  const [visible, setVisible] = useState(false);
  const hasOpen = useMakeHasReadGuideByKey(CLOSE_WEBPUSH_SUBSCRIBE_MODAL);
  const setGuideStatus = useActions(actionsList.setGuideStatus);
  const logEvent = useActions(actionsList.logAnalyticsEvent);
  // Notification.permission will be 'default', 'granted', 'denied'
  // if 'granted' or 'denied', don't show the modal
  const shouldShowModal = shouldShowWebPushSubscribeModal();
  const isFocused = useIsFocused();

  const closeModal = useCallback(() => {
    setGuideStatus({ guideKey: CLOSE_WEBPUSH_SUBSCRIBE_MODAL });
    setVisible(false);
  }, [setGuideStatus]);

  const allowWebPush = async () => {
    closeModal();

    const [err, token] = await safeAwait(getFCMToken(true));

    const event_label = err ? 'error' : !!token;

    if (token) {
      if (memberInfo?.MID) {
        registerBrowserToken({
          browserToken: token,
          email: memberInfo.email,
          userId: memberInfo.MID,
        });
      } else {
        saveBrowserToken(token);
      }
    }

    logEvent({
      logEvent: AnalyticsEvent.GeneralEvent,
      data: {
        action_name: 'requestPushPermissions',
        event_category: 'requestPushPermissions',
        event_label,
      },
    });
  };

  useEffect(() => {
    let setVisibleTimeout;

    if (!hasOpen) {
      if (isFocused) {
        setVisibleTimeout = setTimeout(() => {
          setVisible(true);
        }, 5000);
      } else {
        clearTimeout(setVisibleTimeout);
      }
    }

    return () => setVisibleTimeout && clearTimeout(setVisibleTimeout);
  }, [isFocused, hasOpen]);

  if (!shouldShowModal) {
    return null;
  }

  return (
    <Modal
      isVisible={visible}
      backdropColor={maskBackgroundColor}
      style={styles.modalStyle}
      scrollOffset={1}
      onBackdropPress={closeModal}
    >
      <S.ModalWrapper>
        <S.CloseBtn
          accessibilityRole="button"
          accessibilityLabel="close"
          onPress={closeModal}
          logData={{
            actionName: 'click',
            eventCategory: 'webpush-subscribe-modal',
            eventLabel: 'close',
          }}
        >
          <IconCancel size={22} fillColor={neutralDark} />
        </S.CloseBtn>
        <S.Content>
          <S.LogoWrapper>
            <S.LogoImage source={require('./assets/img_webpush_70x70.png')} />
          </S.LogoWrapper>
          <S.TextTitle>
            {translate('webpush-subscribe-modal-title')}
          </S.TextTitle>
          <S.TextHint>
            {translate('webpush-subscribe-modal-content')}
          </S.TextHint>
        </S.Content>
      </S.ModalWrapper>
      <S.ButtonGroup>
        <S.CancelBtn
          accessibilityRole="button"
          accessibilityLabel="cancel"
          onPress={closeModal}
          logData={{
            actionName: 'click',
            eventCategory: 'webpush-subscribe-modal',
            eventLabel: 'cancel',
          }}
        >
          <S.CencelBtnText>
            {translate('webpush-subscribe-modal-negtive')}
          </S.CencelBtnText>
        </S.CancelBtn>
        <S.AllowBtn
          accessibilityRole="button"
          accessibilityLabel="allow"
          onPress={allowWebPush}
          logData={{
            actionName: 'click',
            eventCategory: 'webpush-subscribe-modal',
            eventLabel: 'allow',
          }}
        >
          <S.AllowBtnText>
            {translate('webpush-subscribe-modal-positive')}
          </S.AllowBtnText>
        </S.AllowBtn>
      </S.ButtonGroup>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalStyle: {
    justifyContent: 'flex-end',
    margin: 0,
  },
});

export default WebPushSubscribeModal;
